import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useEffect, useMemo, useRef, useCallback, type ChangeEvent } from 'react'
import TextareaWithLabel from '../textarea'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useLinkedPatients, type LinkedPatient } from '@/services/linkedPatients'
import WarningRoundedIcon from '@/components/ui/icons/warning-rounded'
import { cn } from '@/lib/utils'
import { COLUMN_WIDTH, LinkedBadge } from './linked-patient-data'

interface QuestionProps {
  question: string
  onChange: (question: string, value: string) => void
  type: 'radio' | 'textarea'
  options?: string[]
  followUps?: {
    triggerValue: string
    question: string
    type: string
  }[]
  isEditable: boolean
}

const Question = ({ options, type, followUps, question, onChange, isEditable }: QuestionProps) => {
  const isTextarea = type === 'textarea'
  const { safeguardingQuestions } = useMedicationRequestStore()
  const value = safeguardingQuestions[question]

  return (
    <>
      <div
        className={cn(
          'flex justify-between py-4 gap-20 items-center',
          isTextarea && 'flex-col py-2 gap-4 mb-4 items-start'
        )}
      >
        <p className='text-[#080F30] text-base font-medium leading-[170%] w-1/2'>{question}</p>
        {type === 'radio' ? (
          <div className='flex gap-4 w-1/2 items-start'>
            {options?.map((option, index) => (
              <label key={index} className='flex items-center gap-4 mr-4'>
                <input
                  disabled={!isEditable}
                  type='radio'
                  value={option}
                  checked={value === option}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(question, e.target.value)}
                />
                {option}
              </label>
            ))}
          </div>
        ) : (
          <div className='px-1'>
            <TextareaWithLabel
              disabled={!isEditable}
              value={value ?? ''}
              className='py-2.5 px-3.5 rounded-lg border resize-none border-[#D2D5D9]'
              placeholder='Type your answer here...'
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(question, e.target.value)}
            />
          </div>
        )}
      </div>
      {followUps && (
        <div className='mt-4'>
          {followUps.map((followUp, i) => {
            if (value === followUp.triggerValue) {
              return (
                <Question
                  isEditable={isEditable}
                  key={i}
                  question={followUp.question}
                  type={followUp.type as any}
                  onChange={onChange}
                />
              )
            }
            return null
          })}
        </div>
      )}
    </>
  )
}

const form = [
  {
    question: 'Is this patient suffering from a terminal illness?',
    type: 'radio',
    options: ['No', 'Yes'],
    followUps: [
      {
        triggerValue: 'Yes',
        question: 'What symptoms are not controlled?',
        type: 'textarea'
      }
    ]
  },
  {
    question: 'Does the patient need assistance with their activities of daily living?',
    type: 'radio',
    options: ['No', 'Yes'],
    followUps: [
      {
        triggerValue: 'Yes',
        question:
          'Which activities of daily living are they currently struggling with that have unmet assistance needs?',
        type: 'textarea'
      },
      {
        triggerValue: 'Yes',
        question: 'Who will you refer the patient to for further activities of daily living assistance?',
        type: 'textarea'
      }
    ]
  },
  {
    question: 'Is the patient at risk of harm, abuse or neglect?',
    type: 'radio',
    options: ['No', 'Yes'],
    followUps: [
      {
        triggerValue: 'Yes',
        question: 'Who will you refer the patient to for additional safeguarding?',
        type: 'textarea'
      },
      {
        triggerValue: 'Yes',
        question: 'In what way are they at risk of harm, abuse or neglect?',
        type: 'textarea'
      }
    ]
  },
  {
    question:
      'Do you have any doubts about the patient’s capacity to consent to this treatment (NB: consent is time-specific and decision-specific)?',
    type: 'radio',
    options: ['No', 'Yes'],
    followUps: [
      {
        triggerValue: 'Yes',
        question: 'Who are you involving?',
        type: 'textarea'
      },
      {
        triggerValue: 'Yes',
        question: 'What treatment decision are you making and why is this in their best interests?',
        type: 'textarea'
      }
    ]
  }
]

/** Primary radio questions shown as rows in the linked-patient comparison view. */
const PRIMARY_QUESTIONS = form.map(q => q.question)

const defaultState = {
  'Is this patient suffering from a terminal illness?': 'No',
  'Does the patient need assistance with their activities of daily living?': 'No',
  'Is the patient at risk of harm, abuse or neglect?': 'No',
  'Do you have any doubts about the patient’s capacity to consent to this treatment (NB: consent is time-specific and decision-specific)?':
    'No'
}

interface Props {
  isEditable: boolean
}

const SafeguardingQuestions = ({ isEditable }: Props) => {
  const { setSafeguardingQuestions, safeguardingQuestions } = useMedicationRequestStore()
  const { data } = useCurrentMedication()

  const linkedPatientHash = data?.patient?.linkedPatientHash
  const { data: linkedData } = useLinkedPatients(linkedPatientHash)

  const linkedOthers = useMemo(() => {
    if (!linkedData?.patients || !data?.patient) return []
    return linkedData.patients.filter(lp => lp.hash !== data.patient.id)
  }, [linkedData, data?.patient])

  const handleInputChange = (question: string, value: string) => {
    setSafeguardingQuestions({ ...safeguardingQuestions, [question]: value })
  }

  const previousAnswers = useMemo(
    () => data?.safeguardingQuestions ?? data?.patient?.safeguardingQuestions ?? defaultState,
    [data?.safeguardingQuestions, data?.patient?.safeguardingQuestions]
  )

  useEffect(() => {
    setSafeguardingQuestions(previousAnswers)
  }, [previousAnswers])

  const hasLinkedOthers = linkedOthers.length > 0

  const showExclamationIcon = useMemo(() => {
    if (Object.values(safeguardingQuestions).includes('Yes')) return true
    if (hasLinkedOthers) {
      return linkedOthers.some(lp => PRIMARY_QUESTIONS.some(q => lp.safeguarding_questions?.[q] === 'Yes'))
    }
    return false
  }, [safeguardingQuestions, linkedOthers, hasLinkedOthers])

  return (
    <div id='safeguarding-questions' className='rounded-2xl border border-custom-neutral-700 bg-white p-8'>
      <Accordion type='single' collapsible>
        <AccordionItem className='border-0' value='item'>
          <AccordionTrigger chevronClassName='w-5 h-5 fill-[#4E545E]' className='hover:no-underline'>
            <div className='flex items-center gap-3 mb-4'>
              <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Safeguarding questions</p>
              {showExclamationIcon && <WarningRoundedIcon />}
              {hasLinkedOthers && <LinkedBadge count={linkedOthers.length} />}
            </div>
          </AccordionTrigger>
          <AccordionContent className='pb-0'>
            {hasLinkedOthers ? (
              <LinkedSafeguardingView
                linkedOthers={linkedOthers}
                isEditable={isEditable}
                currentAnswers={safeguardingQuestions}
                onChange={handleInputChange}
              />
            ) : (
              form.map((question, index) => {
                const isLastItem = index === form.length - 1
                return (
                  <div key={index}>
                    <Question
                      isEditable={isEditable}
                      followUps={question.followUps}
                      type={question.type as any}
                      options={question.options}
                      question={question.question}
                      onChange={handleInputChange}
                    />
                    {!isLastItem && <hr className='my-6 border-custom-neutral-200' />}
                  </div>
                )
              })
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export default SafeguardingQuestions

// ── Linked patients multi-column view ─────────────────────────────────────────

function AnswerCell({
  value,
  options,
  onChange,
  isEditable
}: {
  value: string | undefined
  options: string[]
  onChange?: (next: string) => void
  isEditable: boolean
}) {
  return (
    <div className='rounded-lg border border-custom-neutral-200 px-3 py-2 text-sm text-custom-neutral-800 flex gap-4 items-center'>
      {options.map(option => {
        const checked = value === option
        return (
          <label key={option} className='flex items-center gap-2 cursor-pointer'>
            <input
              type='radio'
              className='h-4 w-4 accent-[#153CB7]'
              disabled={!isEditable || !onChange}
              checked={checked}
              value={option}
              onChange={() => onChange?.(option)}
            />
            <span>{option}</span>
          </label>
        )
      })}
    </div>
  )
}

function LinkedSafeguardingView({
  linkedOthers,
  isEditable,
  currentAnswers,
  onChange
}: {
  linkedOthers: LinkedPatient[]
  isEditable: boolean
  currentAnswers: Record<string, string>
  onChange: (question: string, value: string) => void
}) {
  const columnHeaders = useMemo(
    () => ['Patient data', ...linkedOthers.map((_, i) => `Linked Patient ${i + 1}`)],
    [linkedOthers]
  )

  const totalColumns = columnHeaders.length
  const scrollableWidth = totalColumns * COLUMN_WIDTH

  const headerScrollRef = useRef<HTMLDivElement>(null)
  const syncing = useRef(false)

  const syncAllScrolls = useCallback((sourceEl: HTMLDivElement | null) => {
    if (syncing.current || !sourceEl) return
    syncing.current = true
    const scrollLeft = sourceEl.scrollLeft

    if (headerScrollRef.current && headerScrollRef.current !== sourceEl) {
      headerScrollRef.current.scrollLeft = scrollLeft
    }

    const rows = sourceEl.closest('[data-sg-scroll-root]')?.querySelectorAll<HTMLDivElement>('.sg-scroll-row')
    rows?.forEach(row => {
      if (row !== sourceEl) row.scrollLeft = scrollLeft
    })

    syncing.current = false
  }, [])

  return (
    <div data-sg-scroll-root>
      {/* ── Column headers ── */}
      <div className='flex'>
        <div className='shrink-0 pr-4 w-[320px]'>
          <div className='h-9' />
        </div>
        <div
          ref={headerScrollRef}
          onScroll={() => syncAllScrolls(headerScrollRef.current)}
          className='overflow-x-auto min-w-0 sg-scroll-row'
          style={{ scrollbarWidth: 'none' }}
        >
          <div style={{ minWidth: scrollableWidth }} className='flex'>
            {columnHeaders.map((header, colIdx) => (
              <div key={colIdx} style={{ width: COLUMN_WIDTH, minWidth: COLUMN_WIDTH }} className='px-2 shrink-0'>
                <div className='py-2 h-9 flex items-center'>
                  <span className='text-sm font-bold text-[#1D1D1D] whitespace-nowrap'>{header}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Question rows ── */}
      {form.map((question, qIdx) => {
        const isLastRow = qIdx === form.length - 1
        const currentValue = currentAnswers[question.question]
        const showFollowUps =
          question.followUps?.some(f => currentAnswers[question.question] === f.triggerValue) ?? false

        return (
          <div key={question.question}>
            <div className='flex py-2'>
              {/* Sticky question label */}
              <div className='shrink-0 pr-4 flex items-start pt-3 w-[320px]'>
                <span className='text-sm text-[#080F30] font-medium leading-[170%]'>{question.question}</span>
              </div>

              {/* Scrollable per-patient answer cells */}
              <div
                onScroll={e => syncAllScrolls(e.currentTarget)}
                className='overflow-x-auto min-w-0 sg-scroll-row'
                style={isLastRow ? undefined : { scrollbarWidth: 'none' }}
              >
                <div style={{ minWidth: scrollableWidth }} className='flex'>
                  {/* Current patient column — editable */}
                  <div style={{ width: COLUMN_WIDTH, minWidth: COLUMN_WIDTH }} className='px-2 shrink-0'>
                    <AnswerCell
                      value={currentValue}
                      options={question.options ?? []}
                      isEditable={isEditable}
                      onChange={next => onChange(question.question, next)}
                    />
                  </div>

                  {/* Linked patient columns — read-only */}
                  {linkedOthers.map(lp => {
                    const answer = lp.safeguarding_questions?.[question.question]
                    return (
                      <div
                        key={lp.hash}
                        style={{ width: COLUMN_WIDTH, minWidth: COLUMN_WIDTH }}
                        className='px-2 shrink-0'
                      >
                        <AnswerCell value={answer} options={question.options ?? []} isEditable={false} />
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Follow-up text inputs for the current patient only */}
            {showFollowUps && (
              <div className='pl-0 pr-2 pb-2'>
                {question.followUps?.map((followUp, i) =>
                  currentAnswers[question.question] === followUp.triggerValue ? (
                    <Question
                      isEditable={isEditable}
                      key={`${question.question}-${i}`}
                      question={followUp.question}
                      type={followUp.type as any}
                      onChange={onChange}
                    />
                  ) : null
                )}
              </div>
            )}

            {!isLastRow && <hr className='my-4 border-custom-neutral-200' />}
          </div>
        )
      })}
    </div>
  )
}
